/**
 * Ajuste de privacidad del origen (D-008). Se ejecuta EN EL DISPOSITIVO: la
 * coordenada cruda del pin nunca se transmite ni se guarda.
 *
 * 1. Si el pin cae dentro del polígono de un sector del catálogo, se usa el
 *    centroide de ese sector.
 * 2. Si no, se usa el centro de una celda de una cuadrícula fija de
 *    0,0045° (~500 m en latitudes de Venezuela).
 *
 * La cuadrícula es aritmética simple para que TS y Dart den exactamente la
 * misma celda (vectores/privacidad.json).
 */

export const TAMANO_CELDA_GRADOS = 0.0045

export interface Punto {
  lat: number
  lng: number
}

export interface Celda {
  id: string
  centro: Punto
}

const redondear6 = (n: number) => Math.round(n * 1e6) / 1e6

export function celdaDe(p: Punto): Celda {
  const i = Math.floor(p.lat / TAMANO_CELDA_GRADOS)
  const j = Math.floor(p.lng / TAMANO_CELDA_GRADOS)
  return celdaPorIndices(i, j)
}

export function celdaPorIndices(i: number, j: number): Celda {
  return {
    id: `C${i}_${j}`,
    centro: {
      lat: redondear6((i + 0.5) * TAMANO_CELDA_GRADOS),
      lng: redondear6((j + 0.5) * TAMANO_CELDA_GRADOS),
    },
  }
}

export function celdaDesdeId(id: string): Celda | null {
  const m = /^C(-?\d+)_(-?\d+)$/.exec(id)
  if (!m) return null
  return celdaPorIndices(Number(m[1]), Number(m[2]))
}

/** Anillo exterior como [lng, lat][] (orden GeoJSON). */
export type Anillo = readonly (readonly [number, number])[]

/** Punto en polígono por conteo de cruces (anillo exterior + huecos). */
export function puntoEnPoligono(p: Punto, anillos: readonly Anillo[]): boolean {
  let dentro = false
  for (const anillo of anillos) {
    for (let a = 0, b = anillo.length - 1; a < anillo.length; b = a++) {
      const [xi, yi] = anillo[a]!
      const [xj, yj] = anillo[b]!
      const cruza = yi > p.lat !== yj > p.lat && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi
      if (cruza) dentro = !dentro
    }
  }
  return dentro
}

export interface SectorGeo {
  id: string
  nombre: string
  centroide: Punto
  /** MultiPolygon GeoJSON: polígonos → anillos → [lng, lat] */
  poligonos: readonly (readonly Anillo[])[]
}

export interface OrigenAjustado {
  metodo: 'sector' | 'celda'
  sectorId?: string
  sectorNombre?: string
  celda: string
  punto: Punto
}

export function ajustarOrigen(pin: Punto, sectores: readonly SectorGeo[] = []): OrigenAjustado {
  const celda = celdaDe(pin)
  for (const s of sectores) {
    if (s.poligonos.some((pol) => puntoEnPoligono(pin, pol))) {
      return {
        metodo: 'sector',
        sectorId: s.id,
        sectorNombre: s.nombre,
        celda: celdaDe(s.centroide).id,
        punto: { lat: redondear6(s.centroide.lat), lng: redondear6(s.centroide.lng) },
      }
    }
  }
  return { metodo: 'celda', celda: celda.id, punto: celda.centro }
}

/**
 * Heurística: ¿el texto del sector parece una dirección exacta? Se usa para
 * advertir al usuario (no bloquea).
 */
export function pareceDireccionExacta(texto: string): boolean {
  const t = texto.toLowerCase()
  return (
    /\b(casa|apto|apartamento|piso|quinta|edif(icio)?|torre|n[°º]|nro\.?|numero|número)\s*[\w-]*\d/.test(t) ||
    /\b(calle|avenida|av\.?|carrera|vereda)\s+\S+.*\d/.test(t) ||
    /#\s*\d/.test(t)
  )
}

/**
 * Tabla explícita (no NFD) para que Dart produzca exactamente lo mismo.
 */
const SIN_TILDE: Record<string, string> = {
  á: 'a', à: 'a', ä: 'a', â: 'a', é: 'e', è: 'e', ë: 'e', ê: 'e', í: 'i', ì: 'i', ï: 'i', î: 'i',
  ó: 'o', ò: 'o', ö: 'o', ô: 'o', ú: 'u', ù: 'u', ü: 'u', û: 'u', ñ: 'n', ç: 'c',
}

/** Normaliza texto para agrupar: minúsculas, sin tildes, espacios simples. */
export function normalizarTexto(s: string): string {
  return s
    .toLowerCase()
    .replace(/[áàäâéèëêíìïîóòöôúùüûñç]/g, (c) => SIN_TILDE[c] ?? c)
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
